import { Injectable, inject, signal } from '@angular/core';
import { FunctionsService } from './functions.service';
import {
  LemonSetupResponse,
  LemonSubscription,
  UserSubscription,
  UserLicenseStatus,
  LicenseGroup,
  UserData,
  LicenseGetResponse,
  UserSetLicenseEmailResponse,
  UserDeleteLicenseEmailResponse
} from '../../shared/models/license.models';

/**
 * License Data Service
 *
 * Business logic layer for license-related operations
 * Provides methods for:
 * - Fetching Lemon Squeezy products and plans
 * - Getting user subscriptions
 * - Checking license status
 * - Managing license groups (for licensed users)
 */
@Injectable({
  providedIn: 'root'
})
export class LicenseDataService {
  private functionsService = inject(FunctionsService);

  // Global loading and error state
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get complete license information for the authenticated user
   * Calls 'license_get' cloud function which aggregates:
   * - Personal license data
   * - Lemon Squeezy subscriptions
   * - Institutional licenses
   * - Project quotas
   */
  async getLicense(): Promise<LicenseGetResponse> {
    console.log('📡 [LicenseDataService] getLicense() called');
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<LicenseGetResponse>(
        'license_get',
        {}
      );
      console.log('📡 [LicenseDataService] getLicense() response received');
      console.log('📡 [LicenseDataService] registeredEmails in response:', result?.registeredEmails);
      console.log('📡 [LicenseDataService] Full response:', JSON.stringify(result, null, 2));
      return result;
    } catch (err) {
      console.error('❌ [LicenseDataService] getLicense() error:', err);
      const message = err instanceof Error ? err.message : 'Failed to load license information';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get Lemon Squeezy store setup data (products, variants, pricing)
   *
   * @param refresh Force refresh from API (default: use cache)
   * @returns Promise with store setup data
   */
  async getLemonSetup(refresh: boolean = false): Promise<LemonSetupResponse> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<LemonSetupResponse>(
        'lemon_setup',
        { refresh }
      );
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load Lemon Squeezy setup';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get current user's subscriptions
   *
   * @param email User email (optional - uses authenticated user if not provided)
   * @returns Promise with array of subscriptions
   */
  async getUserSubscriptions(email?: string): Promise<UserSubscription[]> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<{ subscriptions: UserSubscription[] }>(
        'lemon_user_subscriptions',
        { email }
      );
      return result.subscriptions || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load subscriptions';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get license status for user identifier (email or UID)
   *
   * @param userIdentifier Email or UID to check
   * @returns Promise with license status
   */
  async getLicenseByUserIdentifier(userIdentifier: string): Promise<UserLicenseStatus> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<UserLicenseStatus>(
        'get_license_by_user_identifier',
        { userIdentifier }
      );
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get license status';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get license groups (if user has license)
   *
   * @returns Promise with array of license groups
   */
  async getLicenseGroups(): Promise<LicenseGroup[]> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<{ groups: LicenseGroup[] }>(
        'list_license_groups',
        {}
      );
      return result.groups || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load license groups';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get members of a license group (if user has access)
   *
   * @param groupId Group ID
   * @returns Promise with array of member data
   */
  async getLicenseGroupMembers(groupId: string): Promise<any[]> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<{ members: any[] }>(
        'list_license_group_members',
        { groupId }
      );
      return result.members || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load group members';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Add member to license group (license owner only)
   *
   * @param groupId Group ID
   * @param userId User ID to add
   * @returns Promise indicating success
   */
  async addGroupMember(groupId: string, userId: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      await this.functionsService.call(
        'set_license_group_member',
        { groupId, userId }
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add group member';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Remove member from license group (license owner only)
   *
   * @param groupId Group ID
   * @param userId User ID to remove
   * @returns Promise indicating success
   */
  async removeGroupMember(groupId: string, userId: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      await this.functionsService.call(
        'delete_license_group_member',
        { groupId, userId }
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove group member';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Add an institutional email address for the current user
   * 
   * @param emailAddress Email address to add (e.g., user@university.edu)
   * @returns Promise with result details
   */
  async addInstitutionalEmail(emailAddress: string): Promise<UserSetLicenseEmailResponse> {
    console.log('📡 [LicenseDataService] addInstitutionalEmail() called with:', emailAddress);
    this.isLoading.set(true);
    this.error.set(null);

    try {
      console.log('📡 [LicenseDataService] Calling cloud function add_institutional_email...');
      const result = await this.functionsService.call<UserSetLicenseEmailResponse>(
        'add_institutional_email',
        { emailAddress }
      );
      console.log('📡 [LicenseDataService] addInstitutionalEmail() result:', result);
      return result;
    } catch (err) {
      console.error('❌ [LicenseDataService] addInstitutionalEmail() error:', err);
      const message = err instanceof Error ? err.message : 'Failed to add institutional email';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Remove an institutional email address from the current user
   * 
   * @param emailAddress Email address to remove
   * @returns Promise with result details
   */
  async removeInstitutionalEmail(emailAddress: string): Promise<UserDeleteLicenseEmailResponse> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.functionsService.call<UserDeleteLicenseEmailResponse>(
        'delete_institutional_email',
        { emailAddress }
      );
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove institutional email';
      this.error.set(message);
      throw err;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.error.set(null);
  }
}
